import { Badge } from "@arcadeai/design-system/components/ui/atoms/badge";
import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@arcadeai/design-system/components/ui/atoms/dialog";
import { Switch } from "@arcadeai/design-system/components/ui/atoms/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tooltip";
import { cn } from "@arcadeai/design-system/lib/utils";
import { ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import {
  useBulkUpdateHooks,
  useGetHooks,
  useGetPlugins,
} from "../../api/hooks";
import type {
  BulkHookConfig,
  HookPoint,
  HookWithPlugin,
  Phase,
} from "../../types";
import { enrichHooksWithPlugins } from "../../utils";

type ReorderHooksModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

// Hook point display info
const HOOK_POINTS: { key: HookPoint; label: string; description: string }[] = [
  {
    key: "tool.access",
    label: "Tool Access",
    description: "Control who can see and use tools",
  },
  {
    key: "tool.pre",
    label: "Pre-Execution",
    description: "Validate inputs before tool runs",
  },
  {
    key: "tool.post",
    label: "Post-Execution",
    description: "Filter or redact tool outputs",
  },
];

// Track changes per hook
type HookChange = {
  priority: number;
  status: "active" | "inactive";
};

// Get phase order for sorting (matches engine logic)
const getPhaseOrder = (
  binding?: "tenant" | "project",
  phase?: Phase
): number => {
  if (binding === "tenant") {
    return phase === "before" ? 1 : 4;
  }
  return phase === "before" ? 2 : 3;
};

type HookItemProps = {
  hook: HookWithPlugin;
  index: number;
  totalInPhase: number;
  changes: HookChange;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onToggleStatus: () => void;
  isFirst: boolean;
  isLast: boolean;
};

const HookItem = ({
  hook,
  changes,
  onMoveUp,
  onMoveDown,
  onToggleStatus,
  isFirst,
  isLast,
}: HookItemProps) => {
  const isOrg = hook.plugin?.binding?.type === "tenant";
  const isActive = changes.status === "active";

  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-lg border p-3 transition-opacity",
        !isActive && "opacity-50"
      )}
    >
      {/* Order controls */}
      <div className="flex flex-col gap-0.5">
        <Button
          className="size-6 p-0"
          disabled={isFirst}
          onClick={onMoveUp}
          size="icon"
          variant="ghost"
        >
          <ChevronUp className="size-4" />
        </Button>
        <Button
          className="size-6 p-0"
          disabled={isLast}
          onClick={onMoveDown}
          size="icon"
          variant="ghost"
        >
          <ChevronDown className="size-4" />
        </Button>
      </div>

      {/* Priority badge */}
      <span className="w-8 text-center font-mono text-muted-foreground text-sm">
        {changes.priority}
      </span>

      {/* Hook info */}
      <div className="flex flex-1 flex-col">
        <span className="font-medium text-sm">
          {hook.plugin?.name ?? hook.name}
        </span>
        <div className="flex items-center gap-2">
          <Badge
            className={cn(
              "text-[10px]",
              isOrg
                ? "border-violet-500/50 bg-violet-500/10 text-violet-700"
                : "border-pink-500/50 bg-pink-500/10 text-pink-700"
            )}
            variant="outline"
          >
            {isOrg ? "Org" : "Project"}
          </Badge>
          {!!isOrg && (
            <span className="text-muted-foreground text-xs">
              {hook.phase === "before" ? "Before" : "After"}
            </span>
          )}
        </div>
      </div>

      {/* Enable/disable toggle */}
      <Tooltip>
        <TooltipTrigger render={<div className="flex items-center gap-2" />}>
          <Switch checked={isActive} onCheckedChange={onToggleStatus} />
        </TooltipTrigger>
        <TooltipContent>
          {isActive ? "Disable this hook" : "Enable this hook"}
        </TooltipContent>
      </Tooltip>
    </div>
  );
};

type HookPointSectionProps = {
  hookPoint: HookPoint;
  label: string;
  description: string;
  hooks: HookWithPlugin[];
  changes: Map<string, HookChange>;
  onMoveUp: (hookId: string) => void;
  onMoveDown: (hookId: string) => void;
  onToggleStatus: (hookId: string) => void;
};

const HookPointSection = ({
  hookPoint,
  label,
  description,
  hooks,
  changes,
  onMoveUp,
  onMoveDown,
  onToggleStatus,
}: HookPointSectionProps) => {
  const [isExpanded, setIsExpanded] = useState(true);

  // Get sorted hooks for this hook point
  const sortedHooks = useMemo(() => {
    const pointHooks = hooks.filter((h) => h.hook_point === hookPoint);
    // Sort by current changes priority
    return [...pointHooks].sort((a, b) => {
      const aChange = changes.get(a.id);
      const bChange = changes.get(b.id);
      const aPriority = aChange?.priority ?? a.priority;
      const bPriority = bChange?.priority ?? b.priority;

      // First sort by phase order
      const aOrder = getPhaseOrder(
        // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
        a.plugin?.binding?.type as "tenant" | "project" | undefined,
        a.phase
      );
      const bOrder = getPhaseOrder(
        // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
        b.plugin?.binding?.type as "tenant" | "project" | undefined,
        b.phase
      );
      if (aOrder !== bOrder) {
        return aOrder - bOrder;
      }

      return aPriority - bPriority;
    });
  }, [hooks, hookPoint, changes]);

  if (sortedHooks.length === 0) {
    return null;
  }

  return (
    <div className="rounded-lg border">
      <button
        className="flex w-full items-center justify-between p-4 text-left"
        onClick={() => setIsExpanded(!isExpanded)}
        type="button"
      >
        <div>
          <h3 className="font-semibold">{label}</h3>
          <p className="text-muted-foreground text-sm">{description}</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary">{sortedHooks.length} hooks</Badge>
          {isExpanded ? (
            <ChevronUp className="size-4" />
          ) : (
            <ChevronDown className="size-4" />
          )}
        </div>
      </button>
      {!!isExpanded && (
        <div className="flex flex-col gap-2 border-t p-4">
          {sortedHooks.map((hook, index) => {
            const hookPhaseOrder = getPhaseOrder(
              // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
              hook.plugin?.binding?.type as "tenant" | "project" | undefined,
              hook.phase
            );
            const prevPhaseOrder =
              index > 0
                ? getPhaseOrder(
                    // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
                    sortedHooks[index - 1].plugin?.binding?.type as
                      | "tenant"
                      | "project"
                      | undefined,
                    sortedHooks[index - 1].phase
                  )
                : undefined;
            const nextPhaseOrder =
              index < sortedHooks.length - 1
                ? getPhaseOrder(
                    // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
                    sortedHooks[index + 1].plugin?.binding?.type as
                      | "tenant"
                      | "project"
                      | undefined,
                    sortedHooks[index + 1].phase
                  )
                : undefined;

            return (
              <HookItem
                changes={
                  changes.get(hook.id) ?? {
                    priority: hook.priority,
                    status: hook.status,
                  }
                }
                hook={hook}
                index={index}
                isFirst={index === 0 || hookPhaseOrder !== prevPhaseOrder}
                isLast={
                  index === sortedHooks.length - 1 ||
                  hookPhaseOrder !== nextPhaseOrder
                }
                key={hook.id}
                onMoveDown={() => onMoveDown(hook.id)}
                onMoveUp={() => onMoveUp(hook.id)}
                onToggleStatus={() => onToggleStatus(hook.id)}
                totalInPhase={sortedHooks.length}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export const ReorderHooksModal = ({
  open,
  onOpenChange,
}: ReorderHooksModalProps) => {
  // Fetch all hooks and plugins from both project and tenant
  const { data: projectPlugins = [] } = useGetPlugins("project");
  const { data: tenantPlugins = [] } = useGetPlugins("tenant");
  const { data: projectHooks = [] } = useGetHooks("project");
  const { data: tenantHooks = [] } = useGetHooks("tenant");

  const bulkUpdateMutation = useBulkUpdateHooks();

  // Combine and deduplicate plugins and hooks by ID
  // Project-level queries may include inherited tenant resources, so we dedupe
  const allPlugins = useMemo(() => {
    const pluginMap = new Map(projectPlugins.map((p) => [p.id, p]));
    for (const p of tenantPlugins) {
      if (!pluginMap.has(p.id)) {
        pluginMap.set(p.id, p);
      }
    }
    return Array.from(pluginMap.values());
  }, [projectPlugins, tenantPlugins]);

  const allHooks = useMemo(() => {
    const hookMap = new Map(projectHooks.map((h) => [h.id, h]));
    for (const h of tenantHooks) {
      if (!hookMap.has(h.id)) {
        hookMap.set(h.id, h);
      }
    }
    return Array.from(hookMap.values());
  }, [projectHooks, tenantHooks]);

  const enrichedHooks = useMemo(
    () => enrichHooksWithPlugins(allHooks, allPlugins),
    [allHooks, allPlugins]
  );

  // Track all changes locally
  const [changes, setChanges] = useState<Map<string, HookChange>>(new Map());

  // Initialize changes from current hooks when modal opens
  useMemo(() => {
    if (open && enrichedHooks.length > 0 && changes.size === 0) {
      const initial = new Map<string, HookChange>();
      for (const hook of enrichedHooks) {
        initial.set(hook.id, {
          priority: hook.priority,
          status: hook.status,
        });
      }
      setChanges(initial);
    }
  }, [open, enrichedHooks, changes.size]);

  // Reset changes when modal closes
  const handleClose = useCallback(() => {
    setChanges(new Map());
    onOpenChange(false);
  }, [onOpenChange]);

  // Get hooks for a specific hook point with current changes applied
  const getHooksForPoint = useCallback(
    (hookPoint: HookPoint) =>
      enrichedHooks
        .filter((h) => h.hook_point === hookPoint)
        .map((h) => ({
          ...h,
          priority: changes.get(h.id)?.priority ?? h.priority,
          status: changes.get(h.id)?.status ?? h.status,
        }))
        .sort((a, b) => {
          const aOrder = getPhaseOrder(
            // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
            a.plugin?.binding?.type as "tenant" | "project" | undefined,
            a.phase
          );
          const bOrder = getPhaseOrder(
            // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
            b.plugin?.binding?.type as "tenant" | "project" | undefined,
            b.phase
          );
          if (aOrder !== bOrder) {
            return aOrder - bOrder;
          }
          return a.priority - b.priority;
        }),
    [enrichedHooks, changes]
  );

  // Move a hook up in priority
  const handleMoveUp = useCallback(
    (hookId: string) => {
      const hook = enrichedHooks.find((h) => h.id === hookId);
      if (!hook) {
        return;
      }

      const hooksInPoint = getHooksForPoint(hook.hook_point);
      const currentIndex = hooksInPoint.findIndex((h) => h.id === hookId);
      if (currentIndex <= 0) {
        return;
      }

      const hookAbove = hooksInPoint[currentIndex - 1];

      // Don't swap across binding/phase groups (org and project are independent)
      const currentPhaseOrder = getPhaseOrder(
        // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
        hook.plugin?.binding?.type as "tenant" | "project" | undefined,
        hook.phase
      );
      const abovePhaseOrder = getPhaseOrder(
        // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
        hookAbove.plugin?.binding?.type as "tenant" | "project" | undefined,
        hookAbove.phase
      );
      if (currentPhaseOrder !== abovePhaseOrder) {
        return;
      }

      const currentChange = changes.get(hookId) ?? {
        priority: hook.priority,
        status: hook.status,
      };
      const aboveChange = changes.get(hookAbove.id) ?? {
        priority: hookAbove.priority,
        status: hookAbove.status,
      };

      // Swap priorities
      setChanges((prev) => {
        const next = new Map(prev);
        next.set(hookId, { ...currentChange, priority: aboveChange.priority });
        next.set(hookAbove.id, {
          ...aboveChange,
          priority: currentChange.priority,
        });
        return next;
      });
    },
    [enrichedHooks, changes, getHooksForPoint]
  );

  // Move a hook down in priority
  const handleMoveDown = useCallback(
    (hookId: string) => {
      const hook = enrichedHooks.find((h) => h.id === hookId);
      if (!hook) {
        return;
      }

      const hooksInPoint = getHooksForPoint(hook.hook_point);
      const currentIndex = hooksInPoint.findIndex((h) => h.id === hookId);
      if (currentIndex >= hooksInPoint.length - 1) {
        return;
      }

      const hookBelow = hooksInPoint[currentIndex + 1];

      // Don't swap across binding/phase groups (org and project are independent)
      const currentPhaseOrder = getPhaseOrder(
        // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
        hook.plugin?.binding?.type as "tenant" | "project" | undefined,
        hook.phase
      );
      const belowPhaseOrder = getPhaseOrder(
        // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
        hookBelow.plugin?.binding?.type as "tenant" | "project" | undefined,
        hookBelow.phase
      );
      if (currentPhaseOrder !== belowPhaseOrder) {
        return;
      }

      const currentChange = changes.get(hookId) ?? {
        priority: hook.priority,
        status: hook.status,
      };
      const belowChange = changes.get(hookBelow.id) ?? {
        priority: hookBelow.priority,
        status: hookBelow.status,
      };

      // Swap priorities
      setChanges((prev) => {
        const next = new Map(prev);
        next.set(hookId, { ...currentChange, priority: belowChange.priority });
        next.set(hookBelow.id, {
          ...belowChange,
          priority: currentChange.priority,
        });
        return next;
      });
    },
    [enrichedHooks, changes, getHooksForPoint]
  );

  // Toggle hook status
  const handleToggleStatus = useCallback(
    (hookId: string) => {
      const hook = enrichedHooks.find((h) => h.id === hookId);
      if (!hook) {
        return;
      }

      setChanges((prev) => {
        const next = new Map(prev);
        const current = prev.get(hookId) ?? {
          priority: hook.priority,
          status: hook.status,
        };
        next.set(hookId, {
          ...current,
          status: current.status === "active" ? "inactive" : "active",
        });
        return next;
      });
    },
    [enrichedHooks]
  );

  // Check if there are any changes
  const hasChanges = useMemo(() => {
    for (const hook of enrichedHooks) {
      const change = changes.get(hook.id);
      if (!change) {
        continue;
      }
      if (change.priority !== hook.priority || change.status !== hook.status) {
        return true;
      }
    }
    return false;
  }, [enrichedHooks, changes]);

  // Build the payload and save
  const handleSave = useCallback(async () => {
    // Group changes by binding type
    const projectConfigs: BulkHookConfig[] = [];
    const tenantConfigs: BulkHookConfig[] = [];

    for (const hook of enrichedHooks) {
      const change = changes.get(hook.id);
      if (!change) {
        continue;
      }

      // Only include if something changed
      if (change.priority === hook.priority && change.status === hook.status) {
        continue;
      }

      const config: BulkHookConfig = {
        plugin_id: hook.plugin_id,
        hook_point: hook.hook_point,
        priority: change.priority,
        status: change.status,
      };

      if (hook.plugin?.binding?.type === "tenant") {
        tenantConfigs.push(config);
      } else {
        projectConfigs.push(config);
      }
    }

    // Execute bulk updates
    const promises: Promise<unknown>[] = [];
    if (projectConfigs.length > 0) {
      promises.push(
        bulkUpdateMutation.mutateAsync({
          payload: { configs: projectConfigs },
          binding: "project",
        })
      );
    }
    if (tenantConfigs.length > 0) {
      promises.push(
        bulkUpdateMutation.mutateAsync({
          payload: { configs: tenantConfigs },
          binding: "tenant",
        })
      );
    }

    await Promise.all(promises);
    handleClose();
  }, [enrichedHooks, changes, bulkUpdateMutation, handleClose]);

  return (
    <Dialog onOpenChange={handleClose} open={open}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Reorder Hooks</DialogTitle>
          <DialogDescription>
            Drag hooks to reorder them. Lower priority numbers execute first.
            Toggle the switch to enable or disable hooks.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-4">
          {HOOK_POINTS.map(({ key, label, description }) => (
            <HookPointSection
              changes={changes}
              description={description}
              hookPoint={key}
              hooks={enrichedHooks}
              key={key}
              label={label}
              onMoveDown={handleMoveDown}
              onMoveUp={handleMoveUp}
              onToggleStatus={handleToggleStatus}
            />
          ))}

          {enrichedHooks.length === 0 && (
            <div className="py-8 text-center text-muted-foreground">
              No hooks configured yet. Create an extension to add hooks.
            </div>
          )}
        </div>

        <DialogFooter>
          <Button onClick={handleClose} type="button" variant="outline">
            Cancel
          </Button>
          <Button
            disabled={!hasChanges || bulkUpdateMutation.isPending}
            onClick={handleSave}
            type="button"
          >
            {!!bulkUpdateMutation.isPending && (
              <Loader2 className="mr-2 size-4 animate-spin" />
            )}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
